// ============================================================================
// Button styles — the one source for every shared button (Design Constitution
// §4 Buttons, C04, C05, F09, §3 radius and tap targets).
// ----------------------------------------------------------------------------
// Primary: brand fill, white text, pill, 700. One per page (C04); program
// colors never fill a button (C05).
// Secondary: white, 1px strong border, pill, 600.
// Danger text: the secondary shape with danger ink — the 'Delete' trigger at
// the bottom of record pages and the far left of modal footers (O07).
// Destructive: danger fill, only on the delete confirm step.
// Disabled (F09): the same style at 45% opacity; the caller shows a one-line
// gray reason nearby and never dims something that is still clickable.
// Every button is at least 44px tall (§3 tap targets). The S.btn* tokens, the
// record-page buttons and the task-modal buttons all spread these objects;
// <Button> renders them with the §2 pop-out.
// ============================================================================
import type {CSSProperties} from 'react';

const buttonBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 44,
  padding: '10px 18px',
  borderRadius: 999,
  fontSize: 14,
  lineHeight: 1.2,
  fontFamily: 'inherit',
  cursor: 'pointer',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
};

export const buttonPrimary: CSSProperties = {
  ...buttonBase,
  border: '1px solid var(--brand)',
  background: 'var(--brand)',
  color: '#fff',
  fontWeight: 700,
};

export const buttonSecondary: CSSProperties = {
  ...buttonBase,
  border: '1px solid var(--border-strong)',
  background: '#fff',
  color: 'var(--ink)',
  fontWeight: 600,
};

export const buttonDangerText: CSSProperties = {
  ...buttonSecondary,
  color: 'var(--danger-ink)',
};

export const buttonDestructive: CSSProperties = {
  ...buttonBase,
  border: '1px solid var(--danger)',
  background: 'var(--danger)',
  color: '#fff',
  fontWeight: 700,
};

export const buttonDisabled: CSSProperties = {
  opacity: 0.45,
  cursor: 'not-allowed',
  boxShadow: 'none',
};

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'destructive';

export const BUTTON_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: buttonPrimary,
  secondary: buttonSecondary,
  danger: buttonDangerText,
  destructive: buttonDestructive,
};
